import React, { useEffect, useMemo, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Link2, Loader2, Settings2, X } from "lucide-react";
import { hrmsPut } from "../../api/api";
import sessionDB from "../../utils/sessionDB";
import { normalizePreferenceKey } from "../../utils/quickLinks";
import { ROUTE_LINKS } from "../../router/navigation";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const fieldClass = "mt-2 min-h-11 w-full min-w-0 rounded-xl border border-border bg-card px-3 py-2.5 text-sm font-normal text-foreground shadow-xs transition-colors hover:border-slate-300 outline-hidden focus:border-primary focus:ring-2 focus:ring-primary/20";

const QUICK_LINK_DELIMITER = "|||";

const quickLinkKeys = Array.from({ length: 8 }, (_, index) => `quickLink${index + 1}`);

function getQuickLinkOptions() {
  const options = [{ key: "", label: "Select a quick link", value: "" }];
  Object.entries(sessionDB.getEnabledServices()).forEach(([service, modules]) => {
    Object.entries(modules || {}).forEach(([module, submodules]) => {
      Object.keys(submodules || {}).forEach((submodule) => {
        const key = `${service}::${module}::${submodule}`;
        if (!Object.prototype.hasOwnProperty.call(ROUTE_LINKS, key)) return;
        options.push({
          key,
          label: submodule,
          value: `${key}${QUICK_LINK_DELIMITER}${ROUTE_LINKS[key]}`,
        });
      });
    });
  });
  return options;
}

function labelFor(key) {
  if (/^quicklink[1-8]$/.test(normalizePreferenceKey(key))) return `Quick Link ${String(key).slice(-1)}`;
  return String(key)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/^./, (letter) => letter.toUpperCase());
}

function fieldsFrom(value, path = []) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return [];
  return Object.entries(value).flatMap(([key, child]) => {
    const childPath = [...path, key];
    if (child && typeof child === "object" && !Array.isArray(child)) {
      return fieldsFrom(child, childPath);
    }
    return [{ path: childPath, key, value: child }];
  });
}

function replaceAtPath(source, path, value) {
  const next = { ...source };
  let cursor = next;
  path.forEach((part, index) => {
    if (index === path.length - 1) cursor[part] = value;
    else {
      cursor[part] = { ...cursor[part] };
      cursor = cursor[part];
    }
  });
  return next;
}

export function UserPreferencesModal({ open, onOpenChange }) {
  const [preferences, setPreferences] = useState({});
  const [quickLinkOptions, setQuickLinkOptions] = useState(getQuickLinkOptions);
  const optionsByField = useMemo(() => ({
    theme: ["light", "dark"],
    uioption: ["horizontalbar", "sidebar"],
    ...Object.fromEntries(quickLinkKeys.map((key) => [normalizePreferenceKey(key), quickLinkOptions])),
  }), [quickLinkOptions]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const initialPreferencesRef = useRef({});
  const fields = useMemo(() => fieldsFrom(preferences), [preferences]);

  useEffect(() => {
    if (!open) return;
    const enabledQuickLinkOptions = getQuickLinkOptions();
    setQuickLinkOptions(enabledQuickLinkOptions);
    const storedPreferences = { ...sessionDB.getEmpPreferences() };
    const existingKeys = new Set(fieldsFrom(storedPreferences).map((field) => normalizePreferenceKey(field.key)));
    quickLinkKeys.forEach((key) => {
      if (!existingKeys.has(normalizePreferenceKey(key))) storedPreferences[key] = "";
    });
    initialPreferencesRef.current = storedPreferences;
    // Upgrade previously saved route-only quick links to the combined format.
    const nextPreferences = fieldsFrom(storedPreferences).reduce((current, field) => {
      if (!/^quicklink[1-8]$/.test(normalizePreferenceKey(field.key))) return current;
      const routeOption = enabledQuickLinkOptions.find((option) => option.key && ROUTE_LINKS[option.key] === field.value);
      return routeOption
        ? replaceAtPath(current, field.path, routeOption.value)
        : current;
    }, storedPreferences);
    setPreferences(nextPreferences);
    setMessage(null);
  }, [open]);

  function updateField(field, event) {
    let value;
    if (optionsByField[normalizePreferenceKey(field.key)]) value = event.target.value;
    else if (typeof field.value === "boolean") value = event.target.checked;
    else if (typeof field.value === "number") value = event.target.value === "" ? "" : Number(event.target.value);
    else if (Array.isArray(field.value)) {
      try {
        value = JSON.parse(event.target.value);
        if (!Array.isArray(value)) throw new Error();
        setMessage(null);
      } catch {
        setMessage({ type: "error", text: `${labelFor(field.key)} must be a valid JSON array.` });
        return;
      }
    } else value = event.target.value;
    setPreferences((current) => replaceAtPath(current, field.path, value));
  }

  async function handleSave(event) {
    event.preventDefault();
    const hasChanges = JSON.stringify(preferences) !== JSON.stringify(initialPreferencesRef.current);
    if (!hasChanges) {
      onOpenChange(false);
      return;
    }

    const empId = sessionDB.getEmpObj().emp_id;
    if (empId === undefined || empId === null || empId === "") {
      setMessage({ type: "error", text: "Employee ID is missing from the browser session." });
      return;
    }

    setSaving(true);
    setMessage(null);
    try {
      await hrmsPut(`/emp-info/update-user-pref/${encodeURIComponent(empId)}`, {
        empPreferences: preferences,
      });
      sessionDB.updateEmpPreferences(preferences);
      window.dispatchEvent(new CustomEvent("emp-preferences-updated", { detail: preferences }));
      onOpenChange(false);
      window.setTimeout(() => window.location.reload(), 0);
    } catch (error) {
      setMessage({ type: "error", text: error.message || "Unable to save user preferences." });
    } finally {
      setSaving(false);
    }
  }

  function renderField(field) {
    const options = optionsByField[normalizePreferenceKey(field.key)]?.map((option) => ({
      value: option.value ?? option,
      label: option.label ?? labelFor(option),
    }));
    const selectedIndex = options?.findIndex((option) => option.value === (field.value ?? ""));
    const selectOption = (index) => {
      setPreferences((current) => replaceAtPath(current, field.path, options[index].value));
    };
    return (
              <label key={field.path.join(".")} className="block text-sm font-medium text-foreground">
                {labelFor(field.key)}
                {options?.length === 2 ? (
                  <span className="mt-2 flex min-h-11 items-center gap-3 font-normal">
                    <span className={selectedIndex === 0 ? "text-foreground" : "per-file-hint"}>{options[0].label}</span>
                    <input
                      type="checkbox"
                      role="switch"
                      aria-label={labelFor(field.key)}
                      aria-checked={selectedIndex === 1}
                      checked={selectedIndex === 1}
                      onChange={(event) => selectOption(event.target.checked ? 1 : 0)}
                      className="peer sr-only"
                    />
                    <span aria-hidden="true" className="relative h-6 w-11 shrink-0 cursor-pointer rounded-full bg-slate-300 transition-colors after:absolute after:left-0.5 after:top-0.5 after:h-5 after:w-5 after:rounded-full after:bg-card after:shadow-xs after:transition-transform peer-checked:bg-primary peer-checked:after:translate-x-5 peer-focus-visible:ring-2 peer-focus-visible:ring-primary peer-focus-visible:ring-offset-2" />
                    <span className={selectedIndex === 1 ? "text-foreground" : "per-file-hint"}>{options[1].label}</span>
                  </span>
                ) : options ? (
                  <Select value={String(selectedIndex >= 0 ? selectedIndex : 0)} onValueChange={(index) => selectOption(Number(index))}>
                    <SelectTrigger className={fieldClass} aria-label={labelFor(field.key)}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="z-60 border-border bg-card text-foreground">
                      {options.map((option, index) => (
                        <SelectItem key={index} value={String(index)}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : typeof field.value === "boolean" ? (
                  <span className="mt-2 flex items-center gap-3 font-normal per-file-hint">
                    <input type="checkbox" checked={field.value} onChange={(event) => updateField(field, event)} className="h-4 w-4 accent-primary" />
                    {field.value ? "Enabled" : "Disabled"}
                  </span>
                ) : Array.isArray(field.value) ? (
                  <textarea className={fieldClass} rows={3} defaultValue={JSON.stringify(field.value, null, 2)} onBlur={(event) => updateField(field, event)} />
                ) : (
                  <input className={fieldClass} type={typeof field.value === "number" ? "number" : "text"} value={field.value ?? ""} onChange={(event) => updateField(field, event)} />
                )}
              </label>
    );
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-slate-950/55 backdrop-blur-xs" />
        <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-50 flex max-h-[90dvh] w-[calc(100%-2rem)] max-w-2xl flex-col -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl border border-border bg-card shadow-2xl focus:outline-hidden">
          <div className="flex items-center justify-between gap-4 shrink-0 border-b border-border px-5 py-5 sm:px-7">
            <div className="flex items-center gap-3">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-slate-100 text-primary"><Settings2 aria-hidden="true" className="h-5 w-5" /></span>
              <div>
                <DialogPrimitive.Title className="text-lg font-semibold tracking-tight text-foreground">User Preferences</DialogPrimitive.Title>
                <DialogPrimitive.Description className="text-sm per-file-hint">Make your workspace feel like yours.</DialogPrimitive.Description>
              </div>
            </div>
            <DialogPrimitive.Close className="grid h-9 w-9 place-items-center rounded-lg text-slate-500 hover:bg-slate-100" aria-label="Close"><X className="h-4 w-4" /></DialogPrimitive.Close>
          </div>

          <form onSubmit={handleSave} className="flex min-h-0 flex-1 flex-col">
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-6 sm:px-7">
              {fields.length === 0 ? (
                <p className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-600">No user preferences are available for this employee.</p>
              ) : (
                <div className="space-y-6">
                  {fields.some((field) => !/^quicklink[1-8]$/.test(normalizePreferenceKey(field.key))) && (
                    <section aria-label="General preferences">
                      <div className="grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2">
                        {fields.filter((field) => !/^quicklink[1-8]$/.test(normalizePreferenceKey(field.key))).map(renderField)}
                      </div>
                    </section>
                  )}
                  {fields.some((field) => /^quicklink[1-8]$/.test(normalizePreferenceKey(field.key))) && (
                    <section aria-label="Quick Links" className="rounded-2xl border border-border bg-muted p-4 sm:p-5">
                      <div className="flex items-start gap-3">
                        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-border bg-card text-primary"><Link2 aria-hidden="true" className="h-4 w-4" /></span>
                        <div>
                          <h3 className="text-sm font-semibold text-foreground">Quick Links</h3>
                          <p className="mt-1 text-sm per-file-hint">Keep your most-used pages within easy reach.</p>
                        </div>
                      </div>
                      <div className="mt-5 grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2">
                        {fields.filter((field) => /^quicklink[1-8]$/.test(normalizePreferenceKey(field.key)))
                          .sort((a, b) => Number(normalizePreferenceKey(a.key).slice(-1)) - Number(normalizePreferenceKey(b.key).slice(-1)))
                          .map(renderField)}
                      </div>
                    </section>
                  )}
                </div>
              )}
            </div>
            {message && <p role="status" className={`mx-5 mb-4 shrink-0 rounded-xl px-4 py-3 text-sm ${message.type === "success" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>{message.text}</p>}

            <div className="flex shrink-0 flex-wrap items-center justify-end gap-3 border-t border-border bg-muted px-5 py-4 sm:px-7">
              <DialogPrimitive.Close type="button" className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Cancel</DialogPrimitive.Close>
              <button type="submit" disabled={saving || fields.length === 0} className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {saving ? "Saving…" : "Save preferences"}
              </button>
            </div>
          </form>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
